'use client';
import React from 'react';

import { TextField, Button } from '@mui/material';
import Link from 'next/link'; // @todo change to custom Link component
// import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import { useCommentsSectionContext } from '../CommentsSectionContext';
import { useAuthContext } from '@/ui/components/AuthProvider/context';
import Avatar from '@/ui/components/Avatar';
import HideBoundary from '@/ui/components/HideBoundary';
import { log } from '@/utils/logger';
import request from '@/utils/queryData/rest/clientRequest';

const Wrapper = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-grow: 1;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;
`;

const Error = styled.div`
  color: red;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 1rem;
`;

function decodeHTMLString(htmlString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.documentElement.textContent || '';
}

interface AddCommentFormProps {
  editCommentId?: number | null;
  parentCommentId?: number | null;
  onSuccessCallback?: () => void;
  initialContent?: string;
}

/**
 * @todo logged user
 */
export default function AddCommentForm({
  editCommentId = null,
  parentCommentId = null,
  onSuccessCallback = () => {},
  initialContent = '',
}: AddCommentFormProps) {
  const { can, actions, isUserLoggedIn, user } = useAuthContext();
  const { postId, reloadComments } = useCommentsSectionContext();
  const translate = useTranslations();

  const [commentContent, setCommentContent] = React.useState(initialContent);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const parentRef = React.useRef(null);
  // const locale = useLocale();

  const onTextFieldChange = React.useCallback(event => {
    setCommentContent(event.target.value);
  }, []);

  const onSubmit = React.useCallback(
    event => {
      event.preventDefault();
      setIsSubmitting(true);
      let body: Record<string, string | number | null> = {
        post: postId,
        parent: parentCommentId,
        content: commentContent,
        // author: user.userid,
      };

      if (editCommentId) {
        body = {
          content: commentContent,
        };
      }

      new request('commentActionFrontend')
        .post({
          id: editCommentId,
          body,
        })
        .then(_data => {
          setCommentContent('');
          reloadComments();
          setError(null);
          onSuccessCallback();
        })
        .catch(error => {
          const errorMessage = error?.message;
          try {
            if (typeof errorMessage === 'string') {
              const parsedError = JSON.parse(errorMessage) || '';
              const parsedErrorMessage = decodeHTMLString(parsedError?.message);
              setError(parsedErrorMessage || parsedError);
            }
          } catch {
            log.error('AddCommentForm: Error parsing error message');
            setError(errorMessage?.message || errorMessage);
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [commentContent]
  );

  if (!postId) {
    log.error('AddCommentForm: postId is required');
    return null;
  }

  if (!isUserLoggedIn || !user) {
    return (
      <Center>
        {translate.rich('comments-login', {
          link: chunks => (
            <>
              <Link href={`${process.env.NEXT_PUBLIC_LOGIN_URL}`}>
                {chunks}
              </Link>
              &nbsp;
            </>
          ),
        })}
      </Center>
    );
  }

  if (!can(actions.add_comment)) {
    return <Center>{translate('comments-notAllowed')}</Center>;
  }

  return (
    <Wrapper ref={parentRef}>
      <HideBoundary minWidth={500} parentRef={parentRef}>
        <Avatar src={user.avatar} size="md" />
      </HideBoundary>
      <Form onSubmit={onSubmit}>
        <TextField
          id="comment"
          label={translate('comments-textfield-label')}
          value={commentContent}
          onChange={onTextFieldChange}
          disabled={isSubmitting}
          multiline
          rows={8}
          variant="outlined"
          fullWidth
          placeholder={translate('comments-textfield-placeholder')}
          minRows={3}
          sx={{
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '1.875rem',
            borderColor: error ? 'red' : 'inherit',
          }}
        />
        {error && <Error>{error}</Error>}
        <Button
          variant="contained"
          color="warning"
          sx={{ alignSelf: 'flex-start' }}
          type="submit"
          disabled={isSubmitting}
        >
          <strong>{translate('comments-button-send')}</strong>
        </Button>
      </Form>
    </Wrapper>
  );
}
